import {
  Button,
  Checkbox,
  Col,
  Form,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  TimePicker,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SectionHeader from "../../../../components/common/SectionHeader";
import SuccessModal from "../../../../components/Report/SuccessModal";
import Loader from "../../../../components/ui/Loader";
import { dbTimeFormat, timeFormat } from "../../../../constants/constants";
import {
  useAddAttendanceSettingMutation,
  useGetAttendanceSettingDataQuery,
} from "../../../../features/attendanceSetting/attendanceSettingApi";
import { useGetLeaveTypeListQuery } from "../../../../features/leaveType/leaveTypeApi";
import { useGetPayrollSettingQuery } from "../../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { GetAttendanceSettingDataResponseType } from "../../../../types/attendance";
import { officeSettingsTabList } from "./officeSettingsTabList";

type ModalType = "SUCCESS";

export default function Attendance() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { open, handleClose, handleOpen } = useModal<any, ModalType>();
  const [isHalfDayEnable, setIsHalfDayEnable] = useState(false);
  const [isGraceEnable, setIsGraceEnable] = useState(false);
  const [isCheckInOutEnable, setIsCheckInOutEnable] = useState(false);
  const [isAbsentCheckEnable, setIsAbsentCheckEnable] = useState(false);
  const [isForLateCheckIn, setIsForLateCheckIn] = useState(false);
  const [isForEarlyCheckout, setIsForEarlyCheckout] = useState(false);
  const { isLoading, isError, error, data } = useGetAttendanceSettingDataQuery(
    {}
  );
  const [
    addAttendanceSetting,
    {
      isLoading: isAddLoading,
      isError: isAddError,
      error: addError,
      isSuccess: isAddSuccess,
    },
  ] = useAddAttendanceSettingMutation();
  useError(isError, error, "Get optional setting data fail");
  useError(isAddError, addError, "Update optional setting fail");
  const {
    data: leaveData,
    isLoading: isLeaveLoading,
    isError: isLeaveError,
    error: leaveError,
  } = useGetLeaveTypeListQuery({
    params: {
      limit: 0,
      skip: 0,
      // is_published: 1,
    },
  });
  const {
    data: salaryData,
    isLoading: isSalaryLoading,
    isError: isSalaryError,
    error: salaryError,
  } = useGetPayrollSettingQuery();
  useError(isLeaveError, leaveError, "Get lave data fail");
  useError(isSalaryError, salaryError, "Get salary data fail");

  const [salaryList, setSalaryList] = useState([
    { title: "Gross Salary", id: "gross" },
  ]);

  const leaveAdjustmentData = leaveData?.data?.leave_types?.map(
    (item: any) => ({
      label: item?.title,
      value: item?.id?.toString(),
    })
  );

  const salaryAdjustmentData = salaryList?.map((item: any) => ({
    label: item?.title,
    value: item?.id,
  }));

  const getSalaryData = salaryData?.data?.payroll_setting?.salary_breakdown;

  useEffect(() => {
    if (getSalaryData?.length) {
      setSalaryList((prev) => {
        const newItems = getSalaryData.filter(
          (item: any) => !prev.some((prevItem) => prevItem.id === item.id)
        );

        return [
          ...prev,
          ...newItems.map((item: any) => ({
            title: item?.title || "",
            id: item?.id?.toString(),
          })),
        ];
      });
    }
  }, [getSalaryData]);

  type PolicyRules =
    GetAttendanceSettingDataResponseType["data"]["office_settings_attendance"];

  // console.log(data);

  const onFinish = (values: any) => {
    const {
      start_time,
      end_time,
      is_start_grace_time_enable,
      is_end_grace_time_enable,
      half_day_configuration,
      checkin_checkout_policy_rules,
      absent_policy_rules,
      grace_policy_rules,
      ...rest
    } = values;

    const newValues = {
      ...rest,
      office_hour_type: "Fixed Time",
      start_time: start_time?.format(dbTimeFormat),
      end_time: end_time?.format(dbTimeFormat),
      is_start_grace_time_enable: is_start_grace_time_enable ? 1 : 0,
      is_end_grace_time_enable: is_end_grace_time_enable ? 1 : 0,
      is_half_day_enable: isHalfDayEnable ? 1 : 0,
      is_for_late_checkin: isForLateCheckIn,
      is_for_early_checkout: isForEarlyCheckout,
      // half_day_configuration: Object.entries(half_day_configuration || {}).map(
      //   ([key, value]) => {
      //     const halfDayConfig = value as {
      //       start_time?: Dayjs;
      //       end_time?: Dayjs;
      //     };
      //     return {
      //       [key]: {
      //         start_time: halfDayConfig?.start_time?.format(dbTimeFormat),
      //         end_time: halfDayConfig?.end_time?.format(dbTimeFormat),
      //       },
      //     };
      //   }
      // ),
      half_day_configuration: Object.entries(
        half_day_configuration || {}
      ).reduce((acc, [key, value]) => {
        const halfDayConfig = value as {
          start_time?: Dayjs;
          end_time?: Dayjs;
        };

        acc[key] = {
          start_time: halfDayConfig?.start_time?.format(dbTimeFormat) || null,
          end_time: halfDayConfig?.end_time?.format(dbTimeFormat) || null,
        };

        return acc;
      }, {} as Record<string, { start_time: string | null; end_time: string | null }>),
      is_grace_period_policy_enable: isGraceEnable ? 1 : 0,
      is_late_checkin_early_checkout_policy_enable: isCheckInOutEnable ? 1 : 0,
      is_absent_policy_enable: isAbsentCheckEnable ? 1 : 0,
      grace_policy_rules: grace_policy_rules
        ?.filter((item: any) => item)
        ?.map((item: PolicyRules["grace_policy_rules"][0]) => ({
          from_days: item?.from_days,
          to_days: item?.to_days,
          action: item?.action,
          penalty_type: item?.penalty_type,
          penalty_amount: Number(item?.penalty_amount || 0),
        })),
      checkin_checkout_policy_rules: checkin_checkout_policy_rules
        ?.filter((item: any) => item)
        ?.map((item: PolicyRules["checkin_checkout_policy_rules"][0]) => ({
          from_days: item?.from_days,
          to_days: item?.to_days,
          action: item?.action,
          penalty_type: item?.penalty_type,
          penalty_amount: Number(item?.penalty_amount || 0),
        })),
      absent_policy_rules: absent_policy_rules
        ?.filter((item: any) => item)
        ?.map((item: PolicyRules["absent_policy_rules"][0]) => ({
          from_days: item?.from_days,
          to_days: item?.to_days,
          action: item?.action,
          penalty_type: item?.penalty_type,
          penalty_amount: Number(item?.penalty_amount || 0),
        })),
    };

    if (newValues.start_time === newValues.end_time) {
      toast("error", "Start time and end time can't be the same.");
      return;
    }

    // if (newValues.start_time >= newValues.end_time) {
    //   toast("error", "Start time must be earlier than end time.");
    //   return;
    // }

    if (newValues.grace_policy_rules) {
      for (const rule of newValues.grace_policy_rules) {
        if (rule.grace_time < 0) {
          toast("error", "Grace time cannot be negative.");
          return;
        }
        if (rule.grace_time > 1440) {
          toast("error", "Grace time cannot exceed 1440 minutes (24 hours).");
          return;
        }
      }
    }

    addAttendanceSetting(newValues);
  };

  const grace_policy_rules = Form.useWatch("grace_policy_rules", form);
  const gracePolicyAction = (index: number) => {
    return grace_policy_rules?.[index]?.["action"];
  };

  const checkin_checkout_policy_rules = Form.useWatch(
    "checkin_checkout_policy_rules",
    form
  );
  const checkInOutPolicyAction = (index: number) => {
    return checkin_checkout_policy_rules?.[index]?.["action"];
  };
  const absent_policy_rules = Form.useWatch("absent_policy_rules", form);
  const absentPolicyAction = (index: number) => {
    return absent_policy_rules?.[index]?.["action"];
  };

  useEffect(() => {
    const detailsData = data?.data?.office_settings_attendance;
    if (detailsData) {
      form.setFieldsValue({
        start_time: detailsData?.start_time
          ? dayjs(detailsData?.start_time, dbTimeFormat)
          : undefined,
        is_start_grace_time_enable: detailsData?.is_start_grace_time_enable
          ? true
          : false,
        start_grace_time: detailsData?.start_grace_time,
        end_time: detailsData?.end_time
          ? dayjs(detailsData?.end_time, dbTimeFormat)
          : undefined,
        is_end_grace_time_enable: detailsData?.is_end_grace_time_enable
          ? true
          : false,
        end_grace_time: detailsData?.end_grace_time,
        half_day_configuration: Object.entries(
          detailsData?.half_day_configuration || {}
        ).reduce((acc, [key, value]) => {
          const halfDayConfig = value as {
            start_time?: string;
            end_time?: string;
          };

          acc[key] = {
            start_time: halfDayConfig?.start_time
              ? dayjs(halfDayConfig?.start_time, dbTimeFormat)
              : null,
            end_time: halfDayConfig?.end_time
              ? dayjs(halfDayConfig?.end_time, dbTimeFormat)
              : null,
          };

          return acc;
        }, {} as Record<string, { start_time: Dayjs | null; end_time: Dayjs | null }>),
        grace_policy_rules: detailsData?.grace_policy_rules,
        checkin_checkout_policy_rules:
          detailsData?.checkin_checkout_policy_rules,
        absent_policy_rules: detailsData?.absent_policy_rules,
      });

      setIsHalfDayEnable(detailsData?.is_half_day_enable ? true : false);
      setIsGraceEnable(
        detailsData?.is_grace_period_policy_enable ? true : false
      );
      setIsCheckInOutEnable(
        detailsData?.is_late_checkin_early_checkout_policy_enable ? true : false
      );
      setIsAbsentCheckEnable(
        detailsData?.is_absent_policy_enable ? true : false
      );
      setIsForLateCheckIn(detailsData?.is_for_late_checkin ? true : false);
      setIsForEarlyCheckout(detailsData?.is_for_early_checkout ? true : false);
    }
  }, [data?.data?.office_settings_attendance, form]);

  // success toast message
  useEffect(() => {
    if (isAddSuccess) {
      toast("success", "Attendance setting successfully updated!");
      handleOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddSuccess]);

  return (
    <SidebarLayout
      title="Office Settings"
      contentClass="!pb-0"
      tabList={officeSettingsTabList}
    >
      {open?.type === "SUCCESS" && (
        <SuccessModal
          type="queue"
          open={open?.open}
          handleClose={handleClose}
        />
      )}
      {isLoading ? (
        <Loader />
      ) : (
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          <div className="bg-white p-6 pb-0 rounded-md">
            <SectionHeader
              title="Attendance Information"
              // buttonText="Reset All"
            />
            <div className="flex gap-6 my-6 custom-radio">
              <h6 className="text-header text-base font-medium">
                Choose Module:
              </h6>
              <Radio
                checked={true}
                className="text-header text-sm font-medium flex items-center cursor-default"
              >
                Fixed Time
                <span className="text-extra-gray text-sm font-normal cursor-default">
                  {" "}
                  - Office starts & ends on a fixed time
                </span>
              </Radio>
            </div>

            <div>
              <Row gutter={16}>
                <Col span={8}>
                  <div className="p-4 rounded-md border-2 border-dashed border-[#D7DFE9]">
                    <Form.Item
                      name="start_time"
                      label="Start Time"
                      className="mb-4"
                      rules={[
                        {
                          required: true,
                          message: "Please enter start time",
                        },
                      ]}
                    >
                      <TimePicker
                        className="w-full"
                        format={timeFormat}
                        use12Hours
                      />
                    </Form.Item>
                    <Form.Item
                      name="is_start_grace_time_enable"
                      className="mb-0"
                      valuePropName="checked"
                    >
                      <Checkbox className="text-header text-sm font-medium">
                        Allow Grace Period
                      </Checkbox>
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.is_start_grace_time_enable !==
                        currentValues.is_start_grace_time_enable
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("is_start_grace_time_enable") ? (
                          <>
                            <Form.Item
                              name="start_grace_time"
                              label="Grace Time (minutes)"
                              className="mb-1 mt-2"
                              rules={[
                                {
                                  validator: async (_: any, value: number) => {
                                    if (!value) {
                                      return Promise.reject(
                                        new Error("Please enter grace time")
                                      );
                                    }

                                    if (Number(value) < 0) {
                                      return Promise.reject(
                                        new Error(
                                          "Grace time can't be negative"
                                        )
                                      );
                                    }
                                    if (Number(value) > 1440) {
                                      return Promise.reject(
                                        new Error(
                                          "Grace time cannot exceed 1440 minutes"
                                        )
                                      );
                                    }
                                  },
                                },
                              ]}
                            >
                              <InputNumber
                                placeholder="Ex: 15 min"
                                className="w-full"
                              />
                            </Form.Item>
                            <p className="text-body-text text-sm font-normal ">
                              Set grace time for office check-in
                            </p>
                          </>
                        ) : null
                      }
                    </Form.Item>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="p-4 rounded-md border-2 border-dashed border-[#D7DFE9]">
                    <Form.Item
                      name="end_time"
                      label="End Time"
                      className="mb-4"
                      rules={[
                        {
                          required: true,
                          message: "Please enter end time",
                        },
                      ]}
                    >
                      <TimePicker className="w-full" format={timeFormat} />
                    </Form.Item>
                    <Form.Item
                      name="is_end_grace_time_enable"
                      className="mb-0"
                      valuePropName="checked"
                    >
                      <Checkbox className="text-header text-sm font-medium">
                        Allow Grace Period
                      </Checkbox>
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.is_end_grace_time_enable !==
                        currentValues.is_end_grace_time_enable
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("is_end_grace_time_enable") ? (
                          <>
                            <Form.Item
                              name="end_grace_time"
                              label="Grace Time (minutes)"
                              className="mb-1 mt-2"
                              rules={[
                                {
                                  validator: async (_: any, value: number) => {
                                    if (!value) {
                                      return Promise.reject(
                                        new Error("Please enter grace time")
                                      );
                                    }

                                    if (Number(value) < 0) {
                                      return Promise.reject(
                                        new Error(
                                          "Grace time can't be negative"
                                        )
                                      );
                                    }
                                    if (Number(value) > 1440) {
                                      return Promise.reject(
                                        new Error(
                                          "Grace time cannot exceed 1440 minutes"
                                        )
                                      );
                                    }
                                  },
                                },
                              ]}
                            >
                              <InputNumber
                                placeholder="Ex: 15 min"
                                className="w-full"
                              />
                            </Form.Item>
                            <p className="text-body-text text-sm font-normal ">
                              Set grace time for office check-out
                            </p>
                          </>
                        ) : null
                      }
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </div>
            <div className="flex items-center gap-6 mb-4 mt-8">
              <h6 className="text-header text-base font-medium w-[130px]">
                Half day:
              </h6>
              <div className="flex items-center gap-2">
                <Switch
                  id="isHalfDayEnable"
                  checked={isHalfDayEnable}
                  onChange={(checked) => setIsHalfDayEnable(checked)}
                />
                <label
                  htmlFor="isHalfDayEnable"
                  className="text-header text-sm font-medium"
                >
                  {isHalfDayEnable ? "Activated" : "Deactivate"}
                </label>
              </div>
            </div>
            {isHalfDayEnable ? (
              <div className="my-8">
                <Row gutter={16}>
                  <Col span={8}>
                    <div className="relative border-2 border-[#D7DFE9] p-4 pt-6 border-dashed rounded-md">
                      <span className="absolute text-[#3caaf9] text-sm font-medium border-2 border-[#D7DFE9] border-dashed top-0 left-0 -translate-y-[50%] rounded-full px-3 py-0.5 bg-white ml-3">
                        First Half
                      </span>
                      <Row gutter={8}>
                        <Col span={12}>
                          <Form.Item
                            name={[
                              "half_day_configuration",
                              "first_half",
                              "start_time",
                            ]}
                            label="Start Time"
                            className="mb-0"
                            rules={[
                              {
                                required: true,
                                message: "Please enter start time",
                              },
                            ]}
                          >
                            <TimePicker
                              className="w-full"
                              format={timeFormat}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name={[
                              "half_day_configuration",
                              "first_half",
                              "end_time",
                            ]}
                            label="End Time"
                            className="mb-0"
                            rules={[
                              {
                                required: true,
                                message: "Please enter end time",
                              },
                            ]}
                          >
                            <TimePicker
                              className="w-full"
                              format={timeFormat}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="relative border-2 border-[#D7DFE9] p-4 pt-6 border-dashed rounded-md">
                      <span className="absolute text-[#3caaf9] !text-sm font-medium border-2 border-[#D7DFE9] border-dashed top-0 left-0 -translate-y-[50%] rounded-full px-3 py-0.5 bg-white ml-3">
                        Second Half
                      </span>
                      <Row gutter={8}>
                        <Col span={12}>
                          <Form.Item
                            name={[
                              "half_day_configuration",
                              "second_half",
                              "start_time",
                            ]}
                            label="Start Time"
                            className="mb-0"
                            rules={[
                              {
                                required: true,
                                message: "Please enter start time",
                              },
                            ]}
                          >
                            <TimePicker
                              className="w-full"
                              format={timeFormat}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name={[
                              "half_day_configuration",
                              "second_half",
                              "end_time",
                            ]}
                            label="End Time"
                            className="mb-0"
                            rules={[
                              {
                                required: true,
                                message: "Please enter end time",
                              },
                            ]}
                          >
                            <TimePicker
                              className="w-full"
                              format={timeFormat}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : null}

            <div className="my-8">
              <Form.List initialValue={[undefined]} name="grace_policy_rules">
                {(fields, { add, remove }) => {
                  return (
                    <>
                      <SectionHeader
                        title="Attendance Grace Period Policy"
                        buttonText="Add More"
                        icon={<AddIcon />}
                        onButtonClick={() => add()}
                        hideButton={!isGraceEnable}
                      />
                      <div className="flex items-center gap-2 my-4">
                        <Switch
                          id="isGraceEnable"
                          checked={isGraceEnable}
                          onChange={(checked) => setIsGraceEnable(checked)}
                        />
                        <div className="text-header text-sm font-medium">
                          <label
                            className="cursor-pointer select-none"
                            htmlFor="isGraceEnable"
                          >
                            {isGraceEnable ? "Enabled " : "Disabled "}
                          </label>
                          <span className="text-extra-gray font-normal">
                            - After defined policy days, rest of the graced
                            attendance will be considered as late attendance
                          </span>
                        </div>
                      </div>

                      {isGraceEnable
                        ? fields.map((field) => {
                            return (
                              <div
                                className="flex items-center justify-between px-3 py-1 border-b-0 last:border-b bg-[#fbfcfd] border border-light attendance-table"
                                key={field?.key}
                              >
                                <div className="flex items-center gap-2.5">
                                  <span className="text-display-body text-base font-medium">
                                    For
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "from_days"]}
                                  >
                                    <InputNumber
                                      style={{ maxWidth: 50 }}
                                      placeholder="-"
                                      size="small"
                                    />
                                  </Form.Item>
                                  <span className="text-display-body text-base font-medium">
                                    to
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "to_days"]}
                                  >
                                    <InputNumber
                                      style={{ maxWidth: 50 }}
                                      placeholder="-"
                                      size="small"
                                    />
                                  </Form.Item>
                                  <span className="text-display-body text-base font-medium">
                                    Days
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "action"]}
                                    initialValue={
                                      grace_policy_rulesList?.[0]?.value
                                    }
                                  >
                                    <Select
                                      showSearch
                                      placeholder="Select"
                                      options={grace_policy_rulesList}
                                      style={{ width: 180 }}
                                      suffixIcon={<SelectDownIcon />}
                                      rootClassName="icon-gray"
                                      size="small"
                                      onChange={() => {
                                        form.setFieldValue(
                                          [
                                            "grace_policy_rules",
                                            field.name,
                                            "penalty_type",
                                          ],
                                          undefined
                                        );
                                        form.setFieldValue(
                                          [
                                            "grace_policy_rules",
                                            field.name,
                                            "penalty_amount",
                                          ],
                                          undefined
                                        );
                                      }}
                                    />
                                  </Form.Item>
                                  {gracePolicyAction(field.name) ===
                                  "leave_adjustment" ? (
                                    <>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_amount"]}
                                      >
                                        <InputNumber
                                          style={{ maxWidth: 50 }}
                                          placeholder="-"
                                          size="small"
                                        />
                                      </Form.Item>
                                      <span className="text-display-body text-base font-medium">
                                        Days of
                                      </span>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_type"]}
                                        initialValue={
                                          leaveAdjustmentData?.[0]?.value
                                        }
                                      >
                                        <Select
                                          showSearch
                                          placeholder="Select"
                                          options={leaveAdjustmentData}
                                          style={{ width: 180 }}
                                          suffixIcon={<SelectDownIcon />}
                                          rootClassName="icon-gray"
                                          size="small"
                                          loading={isLeaveLoading}
                                        />
                                      </Form.Item>
                                    </>
                                  ) : gracePolicyAction(field.name) ===
                                    "salary_adjustment" ? (
                                    <>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_amount"]}
                                      >
                                        <InputNumber
                                          style={{ maxWidth: 50 }}
                                          placeholder="-"
                                          size="small"
                                        />
                                      </Form.Item>
                                      <span className="text-display-body text-base font-medium">
                                        Days of
                                      </span>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_type"]}
                                        initialValue={
                                          salaryAdjustmentData?.[0]?.value
                                        }
                                      >
                                        <Select
                                          showSearch
                                          placeholder="Select"
                                          options={salaryAdjustmentData}
                                          style={{ width: 180 }}
                                          suffixIcon={<SelectDownIcon />}
                                          rootClassName="icon-gray"
                                          size="small"
                                          loading={isSalaryLoading}
                                        />
                                      </Form.Item>
                                    </>
                                  ) : gracePolicyAction(field.name) ===
                                    "cash_penalty" ? (
                                    <>
                                      <span className="text-display-body text-base font-medium">
                                        Amount (BDT)
                                      </span>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_amount"]}
                                      >
                                        <InputNumber
                                          style={{ maxWidth: 100 }}
                                          placeholder="-"
                                          size="small"
                                        />
                                      </Form.Item>
                                    </>
                                  ) : null}
                                </div>
                                <Button
                                  className="action-button"
                                  size="small"
                                  icon={<DeleteIcon />}
                                  onClick={() => remove(field.name)}
                                />
                              </div>
                            );
                          })
                        : null}
                    </>
                  );
                }}
              </Form.List>
            </div>

            <div className="my-8">
              <Form.List
                initialValue={[undefined]}
                name="checkin_checkout_policy_rules"
              >
                {(fields, { add, remove }) => {
                  return (
                    <>
                      <SectionHeader
                        title="Late Check-in / Early Check-out Policy"
                        buttonText="Add More"
                        icon={<AddIcon />}
                        onButtonClick={() => add()}
                        hideButton={!isCheckInOutEnable}
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2 my-4 ">
                          <Switch
                            id="lateCheckEnable"
                            checked={isCheckInOutEnable}
                            onChange={(checked) =>
                              setIsCheckInOutEnable(checked)
                            }
                          />
                          <div className="text-header text-sm font-medium">
                            <label
                              className="cursor-pointer select-none"
                              htmlFor="lateCheckEnable"
                            >
                              {isCheckInOutEnable ? "Enabled " : "Disabled "}
                            </label>
                            <span className="text-extra-gray font-normal">
                              - After defined policy days, rest of the late
                              attendances will go under last defined policy
                            </span>
                          </div>
                        </div>

                        <Space align="center">
                          <div className="flex items-center gap-5">
                            <Checkbox
                              className="text-header text-sm font-medium"
                              checked={isForLateCheckIn}
                              onChange={(e) =>
                                setIsForLateCheckIn(e.target.checked)
                              }
                            >
                              Check-in
                            </Checkbox>
                            <Checkbox
                              className="text-header text-sm font-medium"
                              checked={isForEarlyCheckout}
                              onChange={(e) =>
                                setIsForEarlyCheckout(e.target.checked)
                              }
                            >
                              Check-out
                            </Checkbox>
                          </div>
                          {/* <Form.Item
                          className="mb-4"
                          name="is_for_late_checkin"
                          valuePropName="checked"
                        >
                          <Checkbox className="text-header text-sm font-medium">
                            Check-in
                          </Checkbox>
                        </Form.Item>
                        <Form.Item
                          className="mb-4"
                          name="is_for_early_checkout"
                          valuePropName="checked"
                        >
                          <Checkbox className="text-header text-sm font-medium">
                            Check-out
                          </Checkbox>
                        </Form.Item> */}
                        </Space>
                      </div>

                      {isCheckInOutEnable
                        ? fields.map((field) => {
                            return (
                              <div
                                className="flex items-center justify-between px-3 py-1 border-b-0 last:border-b bg-[#fbfcfd] border border-light attendance-table"
                                key={field?.key}
                              >
                                <div className="flex items-center gap-2.5">
                                  <span className="text-display-body text-base font-medium">
                                    For
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "from_days"]}
                                  >
                                    <InputNumber
                                      style={{ maxWidth: 50 }}
                                      placeholder="-"
                                      size="small"
                                    />
                                  </Form.Item>
                                  <span className="text-display-body text-base font-medium">
                                    to
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "to_days"]}
                                  >
                                    <InputNumber
                                      style={{ maxWidth: 50 }}
                                      placeholder="-"
                                      size="small"
                                    />
                                  </Form.Item>
                                  <span className="text-display-body text-base font-medium">
                                    Days
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "action"]}
                                    initialValue={
                                      grace_policy_rulesList?.[0]?.value
                                    }
                                  >
                                    <Select
                                      showSearch
                                      placeholder="Select"
                                      options={grace_policy_rulesList}
                                      style={{ width: 180 }}
                                      suffixIcon={<SelectDownIcon />}
                                      rootClassName="icon-gray"
                                      size="small"
                                      onChange={() => {
                                        form.setFieldValue(
                                          [
                                            "checkin_checkout_policy_rules",
                                            field.name,
                                            "penalty_type",
                                          ],
                                          undefined
                                        );
                                        form.setFieldValue(
                                          [
                                            "checkin_checkout_policy_rules",
                                            field.name,
                                            "penalty_amount",
                                          ],
                                          undefined
                                        );
                                      }}
                                    />
                                  </Form.Item>

                                  {checkInOutPolicyAction(field.name) ===
                                  "leave_adjustment" ? (
                                    <>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_amount"]}
                                      >
                                        <InputNumber
                                          style={{ maxWidth: 50 }}
                                          placeholder="-"
                                          size="small"
                                        />
                                      </Form.Item>
                                      <span className="text-display-body text-base font-medium">
                                        Days of
                                      </span>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_type"]}
                                        initialValue={
                                          leaveAdjustmentData?.[0]?.value
                                        }
                                      >
                                        <Select
                                          showSearch
                                          placeholder="Select"
                                          options={leaveAdjustmentData}
                                          style={{ width: 180 }}
                                          suffixIcon={<SelectDownIcon />}
                                          rootClassName="icon-gray"
                                          size="small"
                                          loading={isLeaveLoading}
                                        />
                                      </Form.Item>
                                    </>
                                  ) : checkInOutPolicyAction(field.name) ===
                                    "salary_adjustment" ? (
                                    <>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_amount"]}
                                      >
                                        <InputNumber
                                          style={{ maxWidth: 50 }}
                                          placeholder="-"
                                          size="small"
                                        />
                                      </Form.Item>
                                      <span className="text-display-body text-base font-medium">
                                        Days of
                                      </span>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_type"]}
                                        initialValue={
                                          salaryAdjustmentData?.[0]?.value
                                        }
                                      >
                                        <Select
                                          showSearch
                                          placeholder="Select"
                                          options={salaryAdjustmentData}
                                          style={{ width: 180 }}
                                          suffixIcon={<SelectDownIcon />}
                                          rootClassName="icon-gray"
                                          size="small"
                                          loading={isSalaryLoading}
                                        />
                                      </Form.Item>
                                    </>
                                  ) : checkInOutPolicyAction(field.name) ===
                                    "cash_penalty" ? (
                                    <>
                                      <span className="text-display-body text-base font-medium">
                                        Amount (BDT)
                                      </span>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_amount"]}
                                      >
                                        <InputNumber
                                          style={{ maxWidth: 100 }}
                                          placeholder="-"
                                          size="small"
                                        />
                                      </Form.Item>
                                    </>
                                  ) : null}
                                </div>
                                <Button
                                  className="action-button"
                                  size="small"
                                  icon={<DeleteIcon />}
                                  onClick={() => remove(field.name)}
                                />
                              </div>
                            );
                          })
                        : null}
                    </>
                  );
                }}
              </Form.List>
            </div>

            <div className="my-8">
              <Form.List initialValue={[undefined]} name="absent_policy_rules">
                {(fields, { add, remove }) => {
                  return (
                    <>
                      <SectionHeader
                        title="Absent Policy"
                        buttonText="Add More"
                        icon={<AddIcon />}
                        onButtonClick={() => add()}
                        hideButton={!isAbsentCheckEnable}
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2 my-4 ">
                          <Switch
                            id="absentCheckEnable"
                            checked={isAbsentCheckEnable}
                            onChange={(checked) =>
                              setIsAbsentCheckEnable(checked)
                            }
                          />
                          <div className="text-header text-sm font-medium">
                            <label
                              className="cursor-pointer select-none"
                              htmlFor="absentCheckEnable"
                            >
                              {isAbsentCheckEnable ? "Enabled " : "Disabled "}
                            </label>
                            <span className="text-extra-gray font-normal">
                              - After defined policy days, rest of the absent
                              will go under unpaid policy
                            </span>
                          </div>
                        </div>
                      </div>

                      {isAbsentCheckEnable
                        ? fields.map((field) => {
                            return (
                              <div
                                className="flex items-center justify-between px-3 py-1 border-b-0 last:border-b bg-[#fbfcfd] border border-light attendance-table"
                                key={field?.key}
                              >
                                <div className="flex items-center gap-2.5">
                                  <span className="text-display-body text-base font-medium">
                                    For
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "from_days"]}
                                  >
                                    <InputNumber
                                      style={{ maxWidth: 50 }}
                                      placeholder="-"
                                      size="small"
                                    />
                                  </Form.Item>
                                  <span className="text-display-body text-base font-medium">
                                    to
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "to_days"]}
                                  >
                                    <InputNumber
                                      style={{ maxWidth: 50 }}
                                      placeholder="-"
                                      size="small"
                                    />
                                  </Form.Item>
                                  <span className="text-display-body text-base font-medium">
                                    Days
                                  </span>
                                  <Form.Item
                                    className="mb-0"
                                    name={[field.name, "action"]}
                                    initialValue={
                                      absent_policy_rulesList?.[0]?.value
                                    }
                                  >
                                    <Select
                                      showSearch
                                      placeholder="Select"
                                      options={absent_policy_rulesList}
                                      style={{ width: 180 }}
                                      suffixIcon={<SelectDownIcon />}
                                      rootClassName="icon-gray"
                                      size="small"
                                      onChange={() => {
                                        form.setFieldValue(
                                          [
                                            "absent_policy_rules",
                                            field.name,
                                            "penalty_type",
                                          ],
                                          undefined
                                        );
                                        form.setFieldValue(
                                          [
                                            "absent_policy_rules",
                                            field.name,
                                            "penalty_amount",
                                          ],
                                          undefined
                                        );
                                      }}
                                    />
                                  </Form.Item>

                                  {absentPolicyAction(field.name) ===
                                  "leave_adjustment" ? (
                                    <>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_amount"]}
                                      >
                                        <InputNumber
                                          style={{ maxWidth: 50 }}
                                          placeholder="-"
                                          size="small"
                                        />
                                      </Form.Item>
                                      <span className="text-display-body text-base font-medium">
                                        Days of
                                      </span>
                                      <Form.Item
                                        className="mb-0"
                                        name={[field.name, "penalty_type"]}
                                        initialValue={
                                          leaveAdjustmentData?.[0]?.value
                                        }
                                      >
                                        <Select
                                          showSearch
                                          placeholder="Select"
                                          options={leaveAdjustmentData}
                                          style={{ width: 180 }}
                                          suffixIcon={<SelectDownIcon />}
                                          rootClassName="icon-gray"
                                          size="small"
                                          loading={isLeaveLoading}
                                        />
                                      </Form.Item>
                                    </>
                                  ) : null}
                                </div>
                                <Button
                                  className="action-button"
                                  size="small"
                                  icon={<DeleteIcon />}
                                  onClick={() => remove(field.name)}
                                />
                              </div>
                            );
                          })
                        : null}
                    </>
                  );
                }}
              </Form.List>
            </div>

            <div className="sticky bottom-0 py-6 bg-white border-t border-extra-light">
              <Space className="w-full" size={12}>
                <Button
                  className="normal-dark-button modal-button"
                  size="small"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  className="modal-button"
                  type="primary"
                  htmlType="submit"
                  size="small"
                  loading={isAddLoading}
                >
                  Save
                </Button>
              </Space>
            </div>
          </div>
        </Form>
      )}
    </SidebarLayout>
  );
}

const grace_policy_rulesList = [
  { label: "No Penalty", value: "no_penalty" },
  { label: "Leave Adjustment", value: "leave_adjustment" },
  { label: "Salary Adjustment", value: "salary_adjustment" },
  { label: "Cash Penalty", value: "cash_penalty" },
];

const absent_policy_rulesList = [
  { label: "No Penalty", value: "no_penalty" },
  { label: "Leave Adjustment", value: "leave_adjustment" },
];

// const leaveAdjustmentData = [
//   { label: "Annual Leave", value: "annual_leave" },
//   { label: "Sick Leave", value: "sick_leave" },
// ];
// const salaryAdjustmentData = [
//   { label: "Gross Salary", value: "gross_salary" },
//   { label: "Basic Salary", value: "basic_salary" },
// ];
